import { useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarBlank,
  Clock,
  CurrencyRub,
  GearSix,
  House,
  SignOut,
  Storefront,
  UsersThree
} from '@phosphor-icons/react';
import type { Tenant } from '@/lib/tenant';
import { fetchOwnerBookings, updateOwnerBookingStatus, type OwnerBooking } from '@/lib/owner';
import { ownerSignOut } from '@/lib/ownerAuth';
import { PinSettings } from './PinSettings';
import { SalonEditor } from './SalonEditor';
import { ScheduleBlocks } from './ScheduleBlocks';
import { AvailabilityEditor } from './AvailabilityEditor';
import { ClientCRM } from './ClientCRM';

type AdminSection = 'overview' | 'bookings' | 'clients' | 'schedule' | 'salon' | 'access';

const sections: Array<{ id: AdminSection; label: string; icon: typeof House }> = [
  { id: 'overview', label: 'Обзор', icon: House },
  { id: 'bookings', label: 'Записи', icon: CalendarBlank },
  { id: 'clients', label: 'Клиенты', icon: UsersThree },
  { id: 'schedule', label: 'График', icon: Clock },
  { id: 'salon', label: 'Салон', icon: Storefront },
  { id: 'access', label: 'Доступ', icon: GearSix }
];

function bookingDayKey(iso: string) {
  const date = new Date(iso);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function BookingsList({
  rows,
  pending,
  onStatus
}: {
  rows: OwnerBooking[];
  pending: boolean;
  onStatus: (id: string, status: OwnerBooking['status']) => void;
}) {
  const days = useMemo(() => {
    const grouped = new Map<string, OwnerBooking[]>();
    for (const booking of rows) {
      const key = bookingDayKey(booking.starts_at);
      grouped.set(key, [...(grouped.get(key) ?? []), booking]);
    }
    return [...grouped.entries()].map(([key, bookings]) => ({
      key,
      label: new Date(bookings[0].starts_at).toLocaleDateString('ru-RU', {
        weekday: 'long',
        day: '2-digit',
        month: 'long'
      }),
      rows: bookings
    }));
  }, [rows]);

  if (rows.length === 0) {
    return (
      <div className="mnc-admin-empty">
        <CalendarBlank weight="duotone" />
        <b>Записей пока нет</b>
        <span>Новые визиты появятся здесь сразу после оформления клиентом.</span>
      </div>
    );
  }

  return (
    <div className="mnc-owner-days">
      {days.map((day) => (
        <section className="mnc-owner-day" key={day.key}>
          <span className="mnc-day-label">{day.label}</span>
          <div className="mnc-admin-bookings">
            {day.rows.map((booking) => (
              <article className="mnc-admin-booking" key={booking.id}>
                <time dateTime={booking.starts_at}>
                  {new Date(booking.starts_at).toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </time>
                <div>
                  <b>{booking.service_name}</b>
                  <span>{booking.client_name} · {booking.client_phone}</span>
                  {booking.staff_name && <small>{booking.staff_name} · {booking.status === 'done' ? 'Завершена' : booking.status === 'cancelled' ? 'Отменена' : 'Подтверждена'}</small>}
                </div>
                <span className="mnc-owner-booking-actions">
                  {booking.status === 'pending' && <button className="mnc-linklike" disabled={pending} onClick={() => onStatus(booking.id, 'confirmed')}>Подтвердить</button>}
                  {['pending', 'confirmed'].includes(booking.status) && <button className="mnc-linklike" disabled={pending} onClick={() => onStatus(booking.id, 'done')}>Завершить</button>}
                  {['pending', 'confirmed'].includes(booking.status) && <button className="mnc-linklike is-danger" disabled={pending} onClick={() => onStatus(booking.id, 'cancelled')}>Отменить</button>}
                  <strong>{booking.price ? `${booking.price.toLocaleString('ru-RU')} ₽` : '—'}</strong>
                </span>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function OwnerDashboard() {
  const { tenant } = useOutletContext<{ tenant: Tenant }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [section, setSection] = useState<AdminSection>('overview');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['owner-bookings', tenant.id],
    queryFn: () => fetchOwnerBookings(tenant.id)
  });

  const rows = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    [data]
  );
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OwnerBooking['status'] }) => updateOwnerBookingStatus(id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['owner-bookings', tenant.id] })
  });
  const now = new Date();
  const todayKey = bookingDayKey(now.toISOString());
  const upcoming = rows.filter((booking) => new Date(booking.ends_at) >= now && !['cancelled', 'done'].includes(booking.status));
  const revenue = rows.filter((booking) => booking.status === 'done').reduce((sum, booking) => sum + (booking.price ?? 0), 0);
  const todayCount = upcoming.filter((booking) => bookingDayKey(booking.starts_at) === todayKey).length;
  const clientCount = new Set(upcoming.map((booking) => booking.client_phone)).size;
  const completedRows = rows.filter((booking) => booking.status === 'done');
  const cancelledRows = rows.filter((booking) => booking.status === 'cancelled');
  const completedByClient = new Map<string, number>();
  for (const booking of completedRows) completedByClient.set(booking.client_phone, (completedByClient.get(booking.client_phone) ?? 0) + 1);
  const repeatClients = [...completedByClient.values()].filter((count) => count > 1).length;
  const cancellationRate = rows.length ? Math.round(cancelledRows.length / rows.length * 100) : 0;
  const averageTicket = completedRows.length ? Math.round(revenue / completedRows.length) : 0;
  const nextBooking = upcoming[0];

  const signOut = () => {
    void ownerSignOut().then(() => navigate('/admin', { replace: true }));
  };

  if (isLoading) {
    return <main className="mnc-admin-loading"><span />Загружаем кабинет…</main>;
  }

  if (isError) {
    return (
      <main className="mnc-page">
        <h1>Кабинет владельца</h1>
        <p className="mnc-muted">Не удалось загрузить данные. Войдите повторно.</p>
      </main>
    );
  }

  return (
    <main className="mnc-admin-page">
      <aside className="mnc-admin-sidebar">
        <div className="mnc-admin-brand">
          <span>{tenant.name.slice(0, 1)}</span>
          <div><b>{tenant.name}</b><small>Панель управления</small></div>
        </div>

        <nav aria-label="Разделы кабинета">
          {sections.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={section === item.id ? 'is-active' : ''}
                aria-current={section === item.id ? 'page' : undefined}
                onClick={() => setSection(item.id)}
              >
                <Icon weight={section === item.id ? 'fill' : 'regular'} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="mnc-admin-side-footer">
          <Link to="/"><ArrowRight /> Открыть сайт</Link>
          <button onClick={signOut}><SignOut /> Выйти</button>
        </div>
      </aside>

      <div className="mnc-admin-workspace">
        <header className="mnc-admin-topbar">
          <div>
            <span className="mnc-eyebrow">Кабинет владельца</span>
            <h1>{sections.find((item) => item.id === section)?.label}</h1>
          </div>
          <div className="mnc-admin-status"><i /> Синхронизировано</div>
        </header>

        <nav className="mnc-admin-mobile-nav" aria-label="Разделы кабинета">
          {sections.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                aria-label={item.label}
                aria-current={section === item.id ? 'page' : undefined}
                onClick={() => setSection(item.id)}
              >
                <Icon weight={section === item.id ? 'fill' : 'regular'} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {section === 'overview' && (
          <div className="mnc-admin-section">
            <section className="mnc-admin-welcome">
              <div>
                <span className="mnc-eyebrow">Сегодня</span>
                <h2>Добро пожаловать</h2>
                <p>Все важное по салону — на одном экране.</p>
              </div>
              <button className="mnc-btn mnc-btn-ghost" onClick={() => setSection('salon')}>
                Настроить салон <ArrowRight />
              </button>
            </section>

            <div className="mnc-admin-metrics">
              <article><span><CalendarBlank weight="duotone" /></span><div><b>{todayCount}</b><small>визитов сегодня</small></div></article>
              <article><span><Clock weight="duotone" /></span><div><b>{upcoming.length}</b><small>предстоящих записей</small></div></article>
              <article><span><CurrencyRub weight="duotone" /></span><div><b>{revenue.toLocaleString('ru-RU')} ₽</b><small>выручка завершённых визитов</small></div></article>
              <article><span><UsersThree weight="duotone" /></span><div><b>{clientCount}</b><small>клиентов в расписании</small></div></article>
              <article><span><UsersThree weight="duotone" /></span><div><b>{repeatClients}</b><small>повторных клиентов</small></div></article>
              <article><span><CurrencyRub weight="duotone" /></span><div><b>{averageTicket.toLocaleString('ru-RU')} ₽</b><small>средний чек</small></div></article>
              <article><span><CalendarBlank weight="duotone" /></span><div><b>{cancellationRate}%</b><small>доля отмен</small></div></article>
              <article><span><Clock weight="duotone" /></span><div><b>{completedRows.length}</b><small>завершённых визитов</small></div></article>
            </div>

            <section className="mnc-admin-panel">
              <div className="mnc-admin-panel-head">
                <div><span className="mnc-eyebrow">Ближайшее</span><h2>Следующая запись</h2></div>
                <button className="mnc-linklike" onClick={() => setSection('bookings')}>Все записи</button>
              </div>
              {nextBooking ? (
                <article className="mnc-admin-next">
                  <div className="mnc-admin-date-tile">
                    <b>{new Date(nextBooking.starts_at).toLocaleDateString('ru-RU', { day: '2-digit' })}</b>
                    <span>{new Date(nextBooking.starts_at).toLocaleDateString('ru-RU', { month: 'short' })}</span>
                  </div>
                  <div><b>{nextBooking.service_name}</b><span>{nextBooking.client_name} · {nextBooking.client_phone}</span></div>
                  <time>{new Date(nextBooking.starts_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</time>
                </article>
              ) : (
                <div className="mnc-admin-empty mnc-admin-empty-compact"><CalendarBlank weight="duotone" /><span>Ближайших записей нет.</span></div>
              )}
            </section>
          </div>
        )}

        {section === 'bookings' && (
          <section className="mnc-admin-panel mnc-admin-section">
            <div className="mnc-admin-panel-head">
              <div><span className="mnc-eyebrow">Расписание</span><h2>Записи клиентов</h2></div>
              <span className="mnc-admin-count">{rows.length}</span>
            </div>
            <BookingsList rows={rows} pending={statusMutation.isPending} onStatus={(id, status) => statusMutation.mutate({ id, status })} />
            {statusMutation.isError && <p className="mnc-error">{statusMutation.error.message}</p>}
          </section>
        )}

        {section === 'schedule' && <div className="mnc-admin-section"><AvailabilityEditor /><ScheduleBlocks /></div>}
        {section === 'clients' && <div className="mnc-admin-section"><ClientCRM /></div>}
        {section === 'salon' && <div className="mnc-admin-section mnc-admin-editor"><SalonEditor /></div>}
        {section === 'access' && <div className="mnc-admin-section mnc-admin-editor"><PinSettings /></div>}

        <footer className="mnc-admin-mobile-footer">
          <Link to="/"><ArrowRight /> Открыть сайт</Link>
          <button onClick={signOut}><SignOut /> Выйти</button>
        </footer>
      </div>
    </main>
  );
}
